(() => {
  // app/scripts/utils/debounceTime.js
  function debounceTime(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  // app/scripts/components/job-lists-filter.js
  var JobListsFilter = class extends HTMLElement {
    constructor() {
      super();
      this.callbacks = [];
      this.form = this.querySelector("form");
      this.searchInput = this.querySelector(".js-search-input");
      this.sortSelect = this.querySelector(".js-sort-select");
      this.clearInputButton = this.querySelector(".js-clear-input");
    }
    connectedCallback() {
      this.searchInput.addEventListener(
        "input",
        debounceTime((event) => {
          this.callbacks.forEach((callback) => callback(this.getValues()));
        }, 1e3)
      );
      this.sortSelect.addEventListener("change", (event) => {
        this.callbacks.forEach((callback) => callback(this.getValues()));
      });
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.callbacks.forEach((callback) => callback(this.getValues()));
      });
      this.clearInputButton.addEventListener("click", (event) => {
        event.preventDefault();
        this.searchInput.value = "";
        this.callbacks.forEach((callback) => callback(this.getValues()));
      });
    }
    getValues() {
      return Object.fromEntries(new FormData(this.form));
    }
    onChange(callback) {
      this.callbacks.push(callback);
    }
  };
  customElements.define("job-lists-filter", JobListsFilter);

  // app/scripts/components/CardProduct.js
  var { moneyFormat } = theme.settings;
  var {
    getRegionData,
    countDecimals,
    calculateRegionInventoryTotal,
    getPickupQuantity,
    addQuantityAndUnitPrice,
    getEcoFee,
    getCustomerProvinceCode
  } = theme.utils;
  var CardProduct = class extends HTMLElement {
    _price = null;
    _source = null;
    _b2b_key = null;
    constructor() {
      super();
      this.form = this.querySelector("form");
      this.quantity = this.querySelector("quantity-input");
      this.JSONData = JSON.parse(this.querySelector("script[data-json]")?.textContent || "{}");
      this.unitPriceLabel = this.querySelector(".js-unit-price");
      this.selectRegionButton = this.querySelector(".js-select-region");
      this.pricingData = this.JSONData.pricing;
      this.priceLabel = this.querySelector(".js-price-value");
      this.ecoFeeLabels = this.querySelectorAll(".js-eco-fee");
      this.ecoFeeData = this.JSONData.ecoFee;
      this.currentEcoFee = 0;
      this.erpNumber = this.dataset.erpNumber;
      this.oversize = this.dataset.oversize;
      this.PCProducts = this.JSONData.PCProducts;
      this.myPartInput = this.querySelector("add-my-part");
      if (theme.customer.isB2B) {
        const selectedRegion = getRegionData();
        const foundEcoFee = this.resolveEcoFee(selectedRegion);
        this.updateEcoFee(foundEcoFee);
        this.updateLocationName(theme.preferredLocation?.name);
      }
      this.initButtonEvents();
      this.handleSubmit = this.handleSubmit.bind(this);
      this.updateLayoutByRegion = this.updateLayoutByRegion.bind(this);
      this.handlePreferredLocationChange = this.handlePreferredLocationChange.bind(this);
    }
    set price(price) {
      if (isNaN(price)) {
        console.error("Price is not a number");
        return;
      }
      this._price = Number(price);
      this.updatePrice(this._price);
    }
    get price() {
      return this._price + this.currentEcoFee;
    }
    get source() {
      return this._source;
    }
    set source(source) {
      this._source = source;
    }
    get b2b_key() {
      return this._b2b_key;
    }
    set b2b_key(key) {
      this._b2b_key = key;
    }
    getCurrentQuantity() {
      const quantity = Number.parseInt(this.quantity?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }
    async connectedCallback() {
      await customElements.whenDefined("quantity-input");
      this.initEvents();
      if (theme.customer.isB2B) {
        window.addEventListener("preferredLocation:change", this.handlePreferredLocationChange);
        this.quantity?.onChange(this.handleQuantityChange.bind(this));
      }
    }
    disconnectedCallback() {
      this.form?.removeEventListener("submit", this.handleSubmit);
      window.removeEventListener("regionSelected", this.updateLayoutByRegion);
      window.removeEventListener("preferredLocation:change", this.handlePreferredLocationChange);
    }
    initButtonEvents() {
      this.selectRegionButton?.addEventListener(
        "click",
        (e) => {
          e.stopPropagation();
          window.dispatchEvent(new CustomEvent("open-select-region"));
        },
        {}
      );
    }
    get properties() {
      const properties = { _source: this._source };
      if (theme.customer.isB2B) {
        properties._b2b_key = this._b2b_key;
        properties._b2b_price = String(this._price);
        this.myPartInput?.value && (properties._my_part_number = this.myPartInput.value);
      }
      if (this.currentEcoFee) {
        properties._ecofee = this.currentEcoFee;
      }
      if (countDecimals(this._price) > 2) {
        properties._quantity = this.quantity.value;
      }
      return properties;
    }
    async handleSubmit(event) {
      try {
        event.preventDefault();
        this.setState("adding");
        const formData = new FormData(this.form);
        const quantity = +formData.get("quantity");
        const id = this.form.id.value;
        formData.append("sections", "drawer-cart-items,header-cart-popup");
        formData.append("sections_url", `${theme.routes.searchUrl}?q=[variants.id:${id}]`);
        formData.append("properties[_source]", this._source);
        if (theme.customer.isB2B) {
          this.myPartInput?.value ? formData.append("properties[_my_part_number]", this.myPartInput.value) : formData.delete("properties[_my_part_number]");
          formData.append("properties[_b2b_price]", String(this._price));
          formData.append("properties[_b2b_key]", this._b2b_key);
        }
        if (this.currentEcoFee) {
          formData.append("properties[_ecofee]", this.currentEcoFee);
        }
        if (countDecimals(this._price) > 2) {
          addQuantityAndUnitPrice(formData, this._price, quantity);
        }
        const dataToChange = await Cart.checkItemExists({
          id,
          quantity,
          erpNumber: this.erpNumber,
          price: this._price
        });
        if (dataToChange) {
          await Cart.change({
            data: {
              ...dataToChange,
              sections: formData.get("sections"),
              sections_url: formData.get("sections_url")
            },
            action: "add"
          });
        } else {
          await Cart.add(formData);
        }
        if (window.theme.routes.pageType == "cart") {
          window.location.reload();
        }
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    async initEvents() {
      this.form?.addEventListener("submit", this.handleSubmit);
      if (theme.customer.isB2B)
        return;
      const regionSelector = document.querySelector("select-region");
      if (regionSelector) {
        await customElements.whenDefined("select-region");
        window.addEventListener("regionSelected", this.updateLayoutByRegion);
        window.addEventListener("regionDetected", this.updateLayoutByRegion);
      }
      const selectedRegion = getRegionData();
      selectedRegion && this.updateLayoutByRegion({ detail: { regionData: selectedRegion } });
      this.setState("fetched-price");
    }
    handlePreferredLocationChange(event) {
      const { preferredLocation } = event.detail;
      const priceInventory = window.PriceInventory.storage.find(this.erpNumber);
      this.updateLocationName(preferredLocation.name);
      priceInventory && this.updateB2BLayout(priceInventory?.data, preferredLocation);
      const foundRegion = theme.regionData.find((region) => region.title == preferredLocation.region);
      const foundEcoFee = this.resolveEcoFee(foundRegion, preferredLocation?.pc_number);
      this.updateEcoFee(foundEcoFee);
    }
    updateLayoutByRegion({ detail }) {
      const selectedRegion = detail.regionData;
      const { customer_id, location_id } = selectedRegion;
      const foundEcoFee = this.resolveEcoFee(selectedRegion);
      const foundShopLocation = theme.shopLocations.find((location2) => location2.id == location_id);
      const foundPCProduct = this.findPCProductAndUpdateLayout(foundShopLocation?.pc_number);
      const available = this.oversize === "false" && !!foundPCProduct?.InStock;
      const foundPricing = this.pricingData.find((pricing) => pricing.CustomerID == customer_id);
      this.setState(available ? "stock-available" : "stock-unavailable");
      this.updateEcoFee(foundEcoFee);
      if (foundPricing) {
        const sorted = [...foundPricing.Pricing || []].sort((a, b) => a.QtyBreak - b.QtyBreak);
        const { Pricing = 0, Source } = sorted[0] || {};
        const price = Number(Pricing) || 0;
        if (price) {
          this.price = price;
          this._source = Source;
          this.setState("price-available");
        } else {
          this.setState("price-unavailable");
        }
      } else {
        this.setState("price-unavailable");
      }
    }
    updateB2BLayout(priceInventory, shopLocation) {
      const { Quantity, WarehousesQuantities = [] } = priceInventory || {};
      const { b2bDeliveryQuantity } = theme.settings;
      const pickupQuantity = getPickupQuantity(WarehousesQuantities, shopLocation, Quantity);
      const totalInventoryQuantity = b2bDeliveryQuantity === "region" ? calculateRegionInventoryTotal(WarehousesQuantities, shopLocation, Quantity) : pickupQuantity;
      const pricing = PriceInventory.findPricingByQtyBreak(this.erpNumber, this.quantity?.value || 1);
      this.findPCProductAndUpdateLayout(shopLocation.pc_number);
      this.setState("fetched-price");
      if (!pricing) {
        this.setState("price-unavailable");
        return;
      }
      const { Pricing, Source, key } = pricing;
      this.price = +Pricing;
      this.source = Source;
      this.b2b_key = key;
      if (+Pricing === 0) {
        this.setState("price-unavailable");
        return;
      }
      if (totalInventoryQuantity) {
        this.updateTotalInventory(totalInventoryQuantity);
        if (pickupQuantity > 0) {
          this.updatePickupInventory(pickupQuantity);
          this.setState("pickup-available");
        } else {
          this.setState("pickup-unavailable");
        }
      } else {
        this.setState("stock-unavailable");
      }
    }
    findPCProductAndUpdateLayout(pcNumber) {
      if (!pcNumber)
        return;
      const foundPCProduct = this.PCProducts.find((pcProduct) => pcProduct.WarehouseName == pcNumber);
      this.updateQuantityStep(foundPCProduct?.MultipleSaleQty);
      this.updateMinQuantity(foundPCProduct?.MinimumOrderQty);
      return foundPCProduct;
    }
    resolveEcoFee(region, preferredLocationId) {
      const provinceCode = getCustomerProvinceCode(region, preferredLocationId);
      return getEcoFee(this.ecoFeeData, region, provinceCode);
    }
    updateEcoFee(ecoFee) {
      this.currentEcoFee = Number(ecoFee) || 0;
      if (!this.ecoFeeLabels.length)
        return;
      const ecoFeeMoney = theme.settings.moneyFormat.replace("{{amount}}", this.currentEcoFee.toFixed(2));
      this.ecoFeeLabels.forEach((label) => {
        if (this.currentEcoFee) {
          label.textContent = label.dataset.message.replace("{{money}}", ecoFeeMoney);
          label.classList.remove("hidden");
        } else {
          label.classList.add("hidden");
        }
      });
    }
    updatePrice(price) {
      if (!this.priceLabel)
        return;
      const totalPrice = Math.round((price + this.currentEcoFee) * 100) / 100;
      this.priceLabel.textContent = moneyFormat.replace("{{amount}}", totalPrice.toFixed(2));
      this.handleAfterPriceUpdate();
    }
    updateUnitPrice(newUnitPrice) {
      if (!this.unitPriceLabel)
        return;
      if (newUnitPrice) {
        this.unitPriceLabel.textContent = `/ ${newUnitPrice}`;
        this.unitPriceLabel.classList.remove("hidden");
      } else {
        this.unitPriceLabel.classList.add("hidden");
      }
      this.handleAfterPriceUpdate();
    }
    updateTotalInventory(quantity) {
      this.querySelectorAll(".js-total-inventory").forEach((elm) => {
        elm.textContent = quantity;
      });
    }
    updatePickupInventory(quantity) {
      this.querySelectorAll(".js-pickup-inventory").forEach((elm) => {
        elm.textContent = quantity;
      });
    }
    updateQuantityStep(step) {
      if (!this.quantity || !step)
        return;
      this.quantity.updateStep(step);
      if (step > 1) {
        const messageElm = this.querySelector(".js-sold-multiples-message");
        if (messageElm) {
          messageElm.textContent = messageElm.dataset.message.replace("{{ sold_multiples }}", step);
        }
        this.setState("sold-multiples");
      } else {
        this.setState("sold-single");
      }
    }
    updateMinQuantity(number) {
      if (!this.quantity || !number)
        return;
      this.quantity.input.min = number;
    }
    updateLocationName(name) {
      const locationNameElm = this.querySelector(".js-location-name");
      if (!locationNameElm)
        return;
      const locationId = window.gic_customer.WarehouseName;
      const locationName = name || theme.shopLocations.find((location2) => location2.pc_number == locationId)?.name;
      locationNameElm.textContent = locationName;
    }
    handleQuantityChange(newQuantity) {
      const pricing = PriceInventory.findPricingByQtyBreak(this.erpNumber, newQuantity);
      if (!pricing)
        return;
      if (this._price != pricing.Pricing) {
        this.price = pricing.Pricing;
      }
    }
    setState(state) {
      switch (state) {
        case "stock-unavailable":
          this.classList.add("stock-unavailable");
          break;
        case "stock-available":
          this.classList.remove("stock-unavailable");
          break;
        case "pickup-unavailable":
          this.classList.add("pickup-unavailable");
          break;
        case "pickup-available":
          this.classList.remove("pickup-unavailable");
          break;
        case "sold-multiples":
          this.classList.add("sold-multiples");
          break;
        case "sold-single":
          this.classList.remove("sold-multiples");
          break;
        case "fetched-price":
          this.classList.remove("is-fetching-price");
          break;
        case "price-not-found":
          this.classList.remove("is-fetching-price");
          this.classList.add("price-missing");
          break;
        case "price-unavailable":
          this.classList.add("price-unavailable");
          break;
        case "price-available":
          this.classList.remove("price-unavailable");
          break;
        case "deleting":
          this.classList.add("is-loading", "is-deleting");
          break;
        case "adding":
          this.classList.add("is-loading", "is-atc-loading");
          break;
        case "reset":
          this.classList.remove("is-loading", "is-atc-loading", "is-deleting");
      }
    }
    // Override this method in child class
    handleAfterPriceUpdate() {
    }
  };

  // app/scripts/components/CardProductExtended.js
  var { moneyFormat: moneyFormat2 } = theme.settings;
  var CardProductExtended = class extends CardProduct {
    constructor() {
      super();
      this.isAvailable = null;
      this.totalPriceElm = this.querySelector(".js-total-price");
      this.checkbox = this.querySelector("input[type='checkbox']");
    }
    async connectedCallback() {
      await super.connectedCallback();
      this.initDeleteButton();
      this.updateTotalPrice();
      this.quantity.onChange(this.updateTotalPrice.bind(this));
      this.checkbox.addEventListener("change", this.handleCheckboxChange.bind(this));
    }
    updateTotalPrice() {
      this.totalPriceElm.textContent = moneyFormat2.replace("{{amount}}", (this.price * this.quantity.value).toFixed(2));
      this.dispatch("quantity-change");
    }
    handleCheckboxChange(event) {
      this.dispatch("card-item-select");
    }
    // This method is called after the price is updated from CardProduct
    handleAfterPriceUpdate() {
      this.updateTotalPrice();
    }
    initDeleteButton() {
      this.deleteButtons = this.querySelectorAll(".js-delete-btn");
      this.deleteButtons.forEach((button) => {
        button.addEventListener("click", (e) => this.dispatch("delete-item"));
      });
    }
    dispatch(type) {
      switch (type) {
        case "card-item-select":
          this.checkbox.dispatchEvent(
            new CustomEvent("card-item-select", {
              detail: { variantId: this.dataset.variantId },
              bubbles: true
            })
          );
          break;
        case "quantity-change":
          this.dispatchEvent(
            new CustomEvent("card-item-quantity-change", {
              detail: { variantId: this.dataset.variantId, quantity: this.quantity.value },
              bubbles: true
            })
          );
          break;
        case "delete-item":
          this.dispatchEvent(
            new CustomEvent("card-item-delete", { detail: { variantId: this.dataset.variantId }, bubbles: true })
          );
          break;
      }
    }
    setState(state) {
      super.setState(state);
      this.isAvailable = !["stock-unavailable", "price-missing", "price-unavailable"].some((c) => this.classList.contains(c));
      if (!this.classList.contains("is-oversized")) {
        this.isAvailable = true;
      }
      if (this.classList.contains("is-oversized") && theme.customer.isB2B && !this.classList.contains("price-missing")) {
        this.isAvailable = true;
      }
      switch (state) {
        case "price-not-found":
          super.setState("price-unavailable");
          super.setState("stock-unavailable");
          break;
      }
    }
  };
  customElements.define("card-product-extended", CardProductExtended);

  // app/scripts/components/DownloadTemplate.js
  var DownloadTemplate = class extends HTMLElement {
    constructor() {
      super();
      this.addEventListener("click", this.handleDownloadTemplate.bind(this));
    }
    handleDownloadTemplate() {
      if (!XLSX) {
        throw new Error("XLSX is not loaded");
      }
      const data = [
        ["Manufacturer #", "Quantity (mandatory)"],
        ["example: xyz", "1"],
        ["example: ABC-DE123890", "1"]
      ];
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "import-example.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };
  customElements.define("download-template", DownloadTemplate);

  // app/scripts/pagination-component.js
  var PaginationComponent = class extends HTMLElement {
    constructor() {
      super();
      this.callbacks = [];
      this.baseTemplate = this.innerHTML;
      this.init();
    }
    init() {
      const paginationItems = this.querySelectorAll(".js-pagination-item");
      paginationItems.forEach((item) => {
        item.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.callbacks.forEach((callback) => callback(item.dataset.page));
        });
      });
    }
    onChange(callback) {
      this.callbacks.push(callback);
    }
    _getParts(totalPages, currentPage) {
      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }
      const pages = [1];
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      if (start > 2)
        pages.push("ellipsis-left");
      for (let page = start; page <= end; page++)
        pages.push(page);
      if (end < totalPages - 1)
        pages.push("ellipsis-right");
      pages.push(totalPages);
      return pages;
    }
    render({ currentPage = 1, totalPages = 1 } = {}) {
      if (totalPages <= 1) {
        this.innerHTML = "";
        return;
      }
      const parts = this._getParts(totalPages, currentPage);
      if (this.baseTemplate && !this.querySelector(".js-pagination-parts-placeholder")) {
        this.innerHTML = this.baseTemplate;
        this.init();
      }
      const prevItem = this.querySelector(".js-prev-item");
      const prevLink = this.querySelector(".js-prev-link");
      const nextItem = this.querySelector(".js-next-item");
      const nextLink = this.querySelector(".js-next-link");
      const placeholder = this.querySelector(".js-pagination-parts-placeholder");
      const disablePrev = currentPage <= 1;
      const disableNext = currentPage >= totalPages;
      prevItem?.classList.toggle("disable", disablePrev);
      prevItem?.classList.toggle("pointer-events-none", disablePrev);
      prevItem?.classList.toggle("opacity-[0.7]", disablePrev);
      nextItem?.classList.toggle("disable", disableNext);
      nextItem?.classList.toggle("pointer-events-none", disableNext);
      nextItem?.classList.toggle("opacity-[0.7]", disableNext);
      if (prevLink)
        prevLink.dataset.page = Math.max(1, currentPage - 1);
      if (nextLink)
        nextLink.dataset.page = Math.min(totalPages, currentPage + 1);
      if (placeholder) {
        const ul = placeholder.parentElement;
        if (ul) {
          ul.querySelectorAll(".js-pagination-part-item").forEach((node) => node.remove());
          const fragment = document.createDocumentFragment();
          parts.forEach((part) => {
            if (typeof part === "string") {
              fragment.appendChild(this._createEllipsisItem());
              return;
            }
            fragment.appendChild(this._createPageItem(part, part === currentPage));
          });
          ul.insertBefore(fragment, placeholder);
        }
      }
    }
    updateLayout(newPagination) {
      this.innerHTML = newPagination.innerHTML;
      this.init();
    }
    _createPageItem(part, isCurrent) {
      const li = document.createElement("li");
      li.className = isCurrent ? "active inline-block js-pagination-part-item" : "inline-block js-pagination-part-item";
      const a = document.createElement("a");
      a.textContent = part;
      if (isCurrent) {
        a.href = "javascript:;";
        a.className = "flex h-8 min-w-8 p-1 items-center justify-center rounded bg-primary-40 bg-opacity-50 text-color-80";
      } else {
        a.href = "#";
        a.className = "flex h-8 min-w-8 p-1 items-center justify-center rounded hover:bg-primary-40 bg-opacity-50 js-pagination-item text-color-80";
        a.dataset.page = part;
        a.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.callbacks.forEach((callback) => callback(String(part)));
        });
      }
      li.appendChild(a);
      return li;
    }
    _createEllipsisItem() {
      const li = document.createElement("li");
      li.className = "inline-block js-pagination-part-item";
      const span = document.createElement("span");
      span.innerHTML = "&hellip;";
      li.appendChild(span);
      return li;
    }
  };
  if (!customElements.get("pagination-component")) {
    customElements.define("pagination-component", PaginationComponent);
  }

  // app/scripts/utils/dataToXLSX.js
  function exportToXLSX(data, headers, name, tabName) {
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" });
    XLSX.utils.sheet_add_json(worksheet, data, { origin: "A2", skipHeader: true });
    const workbook = XLSX.utils.book_new();
    const cleanTabName = tabName.replace(/[\[\]*?\/\\:]/g, "").substring(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, cleanTabName);
    const cleanFileName = name.replace(/[\\/:*?"<>|]/g, "_");
    XLSX.writeFile(workbook, `${cleanFileName}.xlsx`);
  }

  // app/scripts/modules/job-lists/export-joblist.js
  var { getVariantsInfo } = theme.utils;
  var {
    exportItems: {
      manufacture: manufactureString,
      description: descriptionString,
      quantity: quantityString,
      price: priceString,
      jobListTabName
    }
  } = theme.string;
  async function exportJobList(jobList) {
    const variantIds = jobList.items.map((item) => item.variantGid.split("/").pop());
    const variantInfo = await getVariantsInfo(variantIds);
    const newVariantInfoList = jobList.items.map(({ variantGid, quantity }) => {
      const id = variantGid.split("/").pop();
      const info = variantInfo[id];
      if (!info)
        return null;
      return { ...info, quantity };
    }).filter(Boolean);
    const getPrice = theme.customer.isB2B ? getVariantPriceB2B : getVariantPrice;
    await getPrice(newVariantInfoList);
    const dataToExport = newVariantInfoList.map(({ sku, quantity, manufacture, Price, ecoFee, description }) => {
      const finalPrice = Price != null ? (Price + (ecoFee || 0)).toFixed(2) : null;
      return {
        [manufactureString]: manufacture,
        [quantityString]: quantity,
        [priceString]: finalPrice ?? "N/A",
        [descriptionString]: description
      };
    });
    exportToXLSX(
      dataToExport,
      [manufactureString, quantityString, priceString, descriptionString],
      jobList.name,
      jobListTabName
    );
  }
  async function getVariantPriceB2B(variantInfoList) {
    await PriceInventory.fetchForVariants(variantInfoList);
    variantInfoList.forEach((variantInfo) => {
      const priceFound = PriceInventory.findPricingByQtyBreak(variantInfo.erpNumber, variantInfo.quantity);
      variantInfo.Price = priceFound?.Pricing ?? null;
    });
  }
  function getVariantPrice(variantInfoList) {
    variantInfoList.forEach((variantInfo) => {
      variantInfo.Price = variantInfo.pplPricing?.Pricing?.[0]?.Pricing ?? null;
    });
  }

  // app/scripts/modules/job-lists/checkPermission.js
  async function checkPermission(jobListId) {
    const jobListInfo = await JobLists.fetchJobList(jobListId);
    const { gid } = theme.customer;
    const { customerGid: ownerGid, customerPermissions, companyPermission } = jobListInfo;
    const { permission = "READ" } = customerPermissions.find((p) => p.customerGid === gid) || {};
    if (gid === ownerGid || companyPermission === "READ_WRITE" || permission === "READ_WRITE") {
      return "owner";
    }
    return "readonly";
  }

  // app/scripts/job-list-details.js
  var PAGE_SIZE = 10;
  var variantIdFromGid = (gid) => (gid || "").split("/").pop();
  var clampToMinQty = (qty, PCProduct) => PCProduct?.MinimumOrderQty > qty ? PCProduct.MinimumOrderQty : qty;
  var resolveEcoFee = (ecoFeeData) => Number(ecoFeeData) || 0;
  var deriveIsAvailable = ({ oversize, PCProduct }) => {
    if (!oversize)
      return true;
    if (theme.customer?.isB2B)
      return true;
    return !!PCProduct?.InStock;
  };
  var {
    debounceTime: debounceTime2,
    string: {
      jobLists: {
        sharedWith: sharedWithString,
        privateList: { yes: privateListYesString, no: privateListNoString }
      }
    },
    utils: { checkAndUpdateQuantity, getVariantsInfo: getVariantsInfo2 }
  } = theme;
  var JobListDetails = class extends HTMLElement {
    constructor() {
      super();
      this.shareMode = document.body.dataset.suffix === "shared-job-list";
      this.jobListId = this.getJobListId();
      this._jobListInfo = null;
      this.productListContainer = this.querySelector(".js-product-list");
      this.pagination = this.querySelector(".js-pagination");
      this.paginationWrapper = this.querySelector(".js-pagination-wrapper");
      this.descriptionElms = this.querySelectorAll(".js-description");
      this.jobListTitleElm = this.querySelector(".js-job-list-title");
      this.updatedAtElm = this.querySelector(".js-updated-at");
      this.privateListElm = this.querySelector(".js-private-list");
      this.membersListElm = this.querySelector(".js-members-list");
      this.updatedByElm = this.querySelector(".js-updated-by");
      this.subtotalElm = this.querySelector(".js-subtotal");
      this.selectedCountElm = this.querySelector(".js-selected-count");
      this.deleteSelectedBtn = this.querySelector(".js-delete-selected");
      this.quantityElm = this.querySelector(".js-quantity");
      this.errorMessageElm = this.querySelector(".js-error");
      this.selectAllCheckbox = this.querySelector("#select-all-input");
      this.productCheckboxForm = this.querySelector("#productCheckboxForm");
      this.searchFilter = this.querySelector("job-lists-filter");
      this.productSelector = this.querySelector("product-selector");
      this.productImporter = this.querySelector("product-importer");
      this.currentPage = 1;
      this.selectedVariants = /* @__PURE__ */ new Set();
      this.cardCache = /* @__PURE__ */ new Map();
      this.filteredItems = [];
    }
    get formCheckboxes() {
      return [...this.productCheckboxForm.elements].filter(
        (input) => input.tagName === "INPUT" && input.name !== "select-all"
      );
    }
    async connectedCallback() {
      await customElements.whenDefined("job-lists-filter");
      await customElements.whenDefined("product-selector");
      await customElements.whenDefined("product-importer");
      await customElements.whenDefined("pagination-component");
      await this.fetchData();
      this.initButtonActions();
      this.pagination.onChange((newPage) => this.goToPage(Number(newPage)));
      this.productSelector.onAdd(this.handleAddItems.bind(this));
      this.searchFilter.onChange(() => this.renderProducts({ resetPage: true }));
      this.productImporter.onUpload(this.handleImportProducts.bind(this));
      this.selectAllCheckbox.addEventListener("change", this.handleSelectAllChange.bind(this));
      this.productCheckboxForm.addEventListener("submit", this.handleSubmit.bind(this));
      this.addEventListener("card-item-delete", this.handleDeleteItem.bind(this));
      this.addEventListener("card-item-select", this.updateSubtotal.bind(this));
      this.addEventListener("card-item-quantity-change", debounceTime2(this.handleCartItemQuantityChange.bind(this), 500));
      this.deleteSelectedBtn.addEventListener("click", this.handleDeleteSelected.bind(this));
    }
    async handleAddItems(selectedItems) {
      try {
        const filteredItems = selectedItems.filter(
          (item) => !this.jobListInfo.items.some((i) => i.variantGid === item.ec_product_id)
        );
        await this.updateJobListItems(filteredItems);
      } catch (error) {
        console.error("Error adding items to job list:", error);
      }
    }
    async handleImportProducts(products) {
      try {
        const filteredItems = products.filter(
          (item) => !this.jobListInfo.items.some((i) => i.variantGid === item.ec_product_id)
        );
        await this.updateJobListItems(filteredItems);
      } catch (error) {
        throw error;
      } finally {
        this.setState("imported");
      }
    }
    async updateJobListItems(newItems) {
      try {
        const itemsToUpdate = [
          ...newItems.map((item) => ({
            handle: item.clickUri.split("/").pop(),
            variantGid: item.ec_product_id,
            productGid: `gid://shopify/Product/${item.ec_item_group_id}`,
            quantity: parseInt(item.quantity) || 1,
            note: "",
            createdAt: (/* @__PURE__ */ new Date()).toISOString(),
            updatedAt: (/* @__PURE__ */ new Date()).toISOString()
          })),
          ...this.jobListInfo.items || []
        ];
        const dataToUpdate = {
          customerGid: this.jobListInfo.customerGid,
          name: this.jobListInfo.name,
          description: this.jobListInfo.description,
          items: itemsToUpdate
        };
        const updatedJobList = await JobLists.update(this.jobListId, dataToUpdate);
        this.jobListInfo = updatedJobList;
        this.updateLayout();
      } catch (error) {
        throw error;
      }
    }
    async fetchData() {
      try {
        this.jobListInfo = this.shareMode ? await JobLists.fetchSharedJobList(this.jobListId) : await JobLists.fetchJobList(this.jobListId);
        await this.updateLayout();
        const state = this.shareMode ? "readonly" : await checkPermission(this.jobListId);
        this.classList.remove("is-fetching");
        this.setState(state);
      } catch (error) {
        this.setState("error", error.message);
        throw error;
      }
    }
    async updateLayout() {
      this.updateInfo();
      await this.renderProducts();
      this.productSelector.setDisabledVariants(
        (this.jobListInfo.items || []).map((item) => variantIdFromGid(item.variantGid)).filter(Boolean)
      );
    }
    async renderProducts({ resetPage = false } = {}) {
      this._renderToken = (this._renderToken || 0) + 1;
      const currentToken = this._renderToken;
      this.cacheCurrentPageData();
      if (resetPage)
        this.currentPage = 1;
      if (!this.jobListInfo?.items?.length) {
        this.dataset.itemsCount = 0;
        this.productListContainer.innerHTML = "";
        this.classList.add("is-empty");
        this.paginationWrapper?.classList.add("hidden");
        return;
      }
      const { search, sort } = this.searchFilter.getValues();
      const sortedItems = [...this.jobListInfo.items].sort((a, b) => {
        const ah = a.handle || "";
        const bh = b.handle || "";
        switch (sort) {
          case "updatedAt:desc":
            return new Date(b.updatedAt) - new Date(a.updatedAt);
          case "updatedAt:asc":
            return new Date(a.updatedAt) - new Date(b.updatedAt);
          case "name:asc":
            return ah.localeCompare(bh);
          case "name:desc":
            return bh.localeCompare(ah);
          default:
            return 0;
        }
      });
      this.filteredItems = sortedItems.filter(
        ({ handle }) => !search || (handle || "").includes(convertToHandle(search))
      );
      const totalPages = Math.ceil(this.filteredItems.length / PAGE_SIZE);
      if (this.currentPage > totalPages)
        this.currentPage = Math.max(1, totalPages);
      const pageStart = (this.currentPage - 1) * PAGE_SIZE;
      const pageItems = this.filteredItems.slice(pageStart, pageStart + PAGE_SIZE);
      const variantQueue = pageItems.map((item) => {
        const variantId = variantIdFromGid(item.variantGid);
        return variantId ? [variantId, item.quantity] : null;
      }).filter(Boolean);
      const cardProductList = [];
      const remaining = [...variantQueue];
      while (remaining.length > 0) {
        const batch = remaining.splice(0, 10);
        const results = await Promise.allSettled(
          batch.map(async ([variantId, quantity]) => {
            const res = await fetch(`${Shopify.routes.root}variants/${variantId}?section_id=card-product-extended`);
            if (!res.ok) {
              throw new Error(`Failed to fetch variant ${variantId}: ${res.status}`);
            }
            const html = await res.text();
            const div = document.createElement("div");
            div.innerHTML = html.replace("[form_id]", "productCheckboxForm");
            const cardProduct = div.querySelector("card-product-extended");
            if (cardProduct) {
              cardProduct.quantity.value = quantity;
            }
            return cardProduct;
          })
        );
        const failedResults = results.filter((result) => result.status === "rejected");
        if (failedResults.length) {
          console.warn("Job list: failed to fetch some variants", failedResults.map((r) => r.reason?.message));
        }
        cardProductList.push(
          ...results.filter((result) => result.status === "fulfilled" && result.value).map((result) => result.value)
        );
      }
      if (currentToken !== this._renderToken)
        return;
      this.dataset.itemsCount = this.filteredItems.length;
      this.classList.toggle("is-empty", this.filteredItems.length === 0);
      this.productListContainer.innerHTML = "";
      this.productListContainer.append(...cardProductList);
      cardProductList.forEach((card) => {
        const variantId = card.dataset.variantId;
        const checkbox = card.querySelector('input[type="checkbox"]');
        if (checkbox && variantId && this.selectedVariants.has(variantId)) {
          checkbox.checked = true;
        }
      });
      this.renderPagination(totalPages);
      await window.PriceInventory.fetchLivePriceInventory();
      if (currentToken !== this._renderToken)
        return;
      this.cacheCurrentPageData();
      this.updateSubtotal();
    }
    renderPagination(totalPages) {
      if (!this.pagination || !this.paginationWrapper)
        return;
      if (totalPages <= 1) {
        this.paginationWrapper.classList.add("hidden");
        return;
      }
      this.paginationWrapper.classList.remove("hidden");
      this.pagination.render({ currentPage: this.currentPage, totalPages });
    }
    goToPage(page) {
      this.currentPage = page;
      this.renderProducts();
    }
    cacheCurrentPageData() {
      this.productListContainer.querySelectorAll("card-product-extended").forEach((card) => {
        const variantId = card.dataset.variantId;
        if (!variantId)
          return;
        this.cardCache.set(variantId, {
          price: card.price || 0,
          quantity: card.quantity?.value || 1,
          isAvailable: card.isAvailable !== false
        });
      });
    }
    async updateInfo() {
      const { name, description, updatedAt, shareable, activityLogs, customerPermissions } = this.jobListInfo;
      const { descriptionElms, updatedAtElm, privateListElm, updatedByElm, membersListElm } = this;
      if (updatedByElm) {
        const lastActivity = activityLogs?.[0];
        const updatedBy = lastActivity?.firstName ? `${lastActivity.firstName} ${lastActivity.lastName || ""}`.trim() : lastActivity?.email;
        updatedByElm.innerHTML = updatedBy || "";
      }
      if (customerPermissions?.length > 0 && membersListElm) {
        membersListElm.innerHTML = customerPermissions.map((permission) => `${permission?.firstName || ""} ${permission?.lastName || ""}`.trim()).filter(Boolean).join("<br>");
      }
      if (privateListElm) {
        privateListElm.innerHTML = customerPermissions?.length ? sharedWithString.replace("{{count}}", customerPermissions.length) : privateListYesString;
      }
      descriptionElms.forEach((elm) => elm.textContent = description ?? "");
      updatedAtElm.textContent = new Date(updatedAt).toLocaleDateString();
      const jobListTitleElms = document.querySelectorAll(".js-job-list-title");
      jobListTitleElms.forEach((elm) => elm.textContent = name);
    }
    get jobListInfo() {
      return this._jobListInfo;
    }
    set jobListInfo(info) {
      this._jobListInfo = info;
      this.setImporterAlreadyExists();
    }
    getJobListId() {
      const searchParams = new URLSearchParams(location.search);
      return searchParams.get("id");
    }
    initButtonActions() {
      const exportButtons = this.querySelectorAll(".js-export-btn");
      const copyButtons = this.querySelectorAll(".js-copy-btn");
      const editButtons = this.querySelectorAll(".js-edit-btn");
      const shareButtons = this.querySelectorAll(".js-share-btn");
      const deleteButtons = this.querySelectorAll(".js-delete-joblist-btn");
      exportButtons.forEach((button) => button.addEventListener("click", this.handleExport.bind(this)));
      editButtons.forEach((button) => button.addEventListener("click", this.handleEdit.bind(this)));
      deleteButtons.forEach((button) => button.addEventListener("click", this.handleDelete.bind(this)));
      copyButtons.forEach((button) => button.addEventListener("click", this.handleCopyList.bind(this)));
      shareButtons.forEach((button) => button.addEventListener("click", () => JobLists.share(this.jobListId)));
    }
    async handleExport() {
      try {
        this.setState("exporting");
        const jobList = this.shareMode ? await JobLists.fetchSharedJobList(this.jobListId) : await JobLists.fetchJobList(this.jobListId);
        await exportJobList(jobList);
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    async handleDelete() {
      try {
        const confirmation = await JobLists.modal.delete.confirm();
        if (!confirmation)
          return;
        JobLists.modal.delete.setLoading(true);
        if (this.isOwner()) {
          await JobLists.delete(this.jobListId);
        } else {
          await JobLists.updateCustomerPermission(this.jobListId, {
            action: "DELETE",
            email: theme.customer.email,
            permission: this.isReadOnly() ? "READ" : "READ_WRITE"
          });
        }
        window.location.href = theme.routes.jobListsUrl;
      } catch (error) {
        throw error;
      } finally {
        JobLists.modal.delete.close(true);
      }
    }
    async handleEdit() {
      const updatedJobList = await JobLists.modal.update.open(this.jobListId);
      if (updatedJobList) {
        this.jobListInfo = updatedJobList;
        this.updateInfo();
      }
    }
    async handleDeleteItem(event) {
      const { target, detail } = event;
      try {
        target.setState("deleting");
        const { variantId } = detail;
        this.selectedVariants.delete(variantId);
        this.cardCache.delete(variantId);
        const dataToUpdate = {
          ...this.jobListInfo,
          items: this.jobListInfo.items.filter((item) => variantIdFromGid(item.variantGid) !== variantId)
        };
        const updatedJobList = await JobLists.update(this.jobListId, dataToUpdate);
        this.jobListInfo = updatedJobList;
        this.updateLayout();
      } catch (error) {
        throw error;
      }
    }
    async handleCopyList() {
      try {
        this.setState("copying");
        await JobLists.modal.copyList.open({ id: this.jobListId });
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    async handleSelectAllChange(event) {
      const { checked } = event.target;
      this.filteredItems.forEach((item) => {
        const variantId = variantIdFromGid(item.variantGid);
        if (variantId) {
          if (checked)
            this.selectedVariants.add(variantId);
          else
            this.selectedVariants.delete(variantId);
        }
      });
      this.formCheckboxes.forEach((input) => {
        input.checked = checked;
      });
      if (checked) {
        this.setState("loading");
        try {
          await this.fetchAllPricingForSelected();
        } catch (error) {
          console.error("Failed to fetch pricing for selected items:", error);
          this.filteredItems.forEach((item) => {
            const variantId = variantIdFromGid(item.variantGid);
            if (variantId)
              this.selectedVariants.delete(variantId);
          });
          this.formCheckboxes.forEach((input) => {
            input.checked = false;
          });
        } finally {
          this.setState("reset");
        }
      }
      this.updateSubtotal();
    }
    async fetchAllPricingForSelected() {
      this.hideError();
      const uncachedItems = this.filteredItems.filter((item) => {
        const variantId = variantIdFromGid(item.variantGid);
        if (!this.selectedVariants.has(variantId))
          return false;
        return !this.cardCache.get(variantId)?.price;
      });
      if (!uncachedItems.length)
        return;
      const variantInfoList = await getVariantsInfo2(uncachedItems.map((item) => variantIdFromGid(item.variantGid)));
      await PriceInventory.fetchForVariants(variantInfoList);
      let failedCount = 0;
      for (const item of uncachedItems) {
        const variantId = variantIdFromGid(item.variantGid);
        const variantInfo = variantInfoList[variantId];
        if (!variantInfo) {
          failedCount++;
          continue;
        }
        const { erpNumber, ecoFee: ecoFeeData, oversize, pplPricing, PCProduct } = variantInfo;
        const quantity = clampToMinQty(item.quantity, PCProduct);
        const pricing = PriceInventory.resolvePricing({ erpNumber, pplPricing }, quantity);
        if (!pricing?.Pricing)
          continue;
        const ecoFee = resolveEcoFee(ecoFeeData);
        const isAvailable = deriveIsAvailable({ oversize, PCProduct });
        this.cardCache.set(variantId, { price: pricing.Pricing + ecoFee, quantity, isAvailable });
      }
      if (failedCount > 0) {
        this.setState("error", `Failed to load pricing for ${failedCount} item(s). The subtotal may be incomplete.`);
      }
    }
    handleCartItemQuantityChange(event) {
      this.cacheCurrentPageData();
      this.updateSubtotal();
      this.updateJobListQuantity(event.detail);
    }
    async updateJobListQuantity(data) {
      try {
        const { variantId, quantity } = data;
        const item = this.jobListInfo.items.find((item2) => variantIdFromGid(item2.variantGid) === variantId);
        if (!item || quantity == item.quantity)
          return;
        item.quantity = quantity;
        const dataToUpdate = {
          customerGid: this.jobListInfo.customerGid,
          name: this.jobListInfo.name,
          description: this.jobListInfo.description,
          items: this.jobListInfo.items.map((item2) => {
            if (variantIdFromGid(item2.variantGid) === variantId) {
              item2.quantity = quantity;
              item2.updatedAt = (/* @__PURE__ */ new Date()).toISOString();
            }
            return item2;
          })
        };
        await JobLists.update(this.jobListId, dataToUpdate);
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    updateSubtotal() {
      this.formCheckboxes.forEach((input) => {
        const variantId = input.value;
        if (input.checked) {
          this.selectedVariants.add(variantId);
        } else {
          this.selectedVariants.delete(variantId);
        }
      });
      let subtotal = 0;
      this.selectedVariants.forEach((variantId) => {
        const cached = this.cardCache.get(variantId);
        if (cached?.price && cached.isAvailable !== false)
          subtotal += cached.price * (cached.quantity || 1);
      });
      this.subtotalElm.innerHTML = theme.settings.moneyFormat.replace("{{amount}}", subtotal.toFixed(2));
      this.dataset.subtotal = subtotal;
      this.selectedCountElm.innerHTML = this.selectedVariants.size;
      this.classList.toggle("is-selecting", this.selectedVariants.size > 0);
      this.updateSelectAllCheckbox();
    }
    updateSelectAllCheckbox() {
      if (!this.filteredItems.length)
        return;
      let allSelected = true;
      let someSelected = false;
      for (const item of this.filteredItems) {
        const isSelected = this.selectedVariants.has(variantIdFromGid(item.variantGid));
        if (!isSelected)
          allSelected = false;
        if (isSelected)
          someSelected = true;
        if (!allSelected && someSelected)
          break;
      }
      this.selectAllCheckbox.checked = allSelected;
      this.selectAllCheckbox.indeterminate = !allSelected && someSelected;
    }
    async handleSubmit(event) {
      event.preventDefault();
      this.setState("atc");
      this.hideError();
      try {
        this.productListContainer.querySelectorAll("quantity-input").forEach((qi) => qi.validateAndDispatch?.());
        this.cacheCurrentPageData();
        const selectedJobListItems = this.jobListInfo.items.filter((item) => {
          const variantId = variantIdFromGid(item.variantGid);
          if (!this.selectedVariants.has(variantId))
            return false;
          const card = this.productListContainer.querySelector(`card-product-extended[data-variant-id="${variantId}"]`);
          if (card)
            return card.isAvailable !== false;
          return this.cardCache.get(variantId)?.isAvailable !== false;
        });
        if (!selectedJobListItems.length)
          return;
        const variantIds = selectedJobListItems.map((item) => variantIdFromGid(item.variantGid));
        const variantInfoList = await getVariantsInfo2(variantIds);
        await PriceInventory.fetchForVariants(variantInfoList);
        const itemsToAdd = [];
        const changesToApply = [];
        await Cart.get();
        for (const item of selectedJobListItems) {
          const variantId = variantIdFromGid(item.variantGid);
          const { ecoFee: ecoFeeData, erpNumber, pplPricing, PCProduct } = variantInfoList[variantId] || {};
          const cached = this.cardCache.get(variantId);
          const quantity = clampToMinQty(cached?.quantity ?? item.quantity, PCProduct);
          const selectedPricing = PriceInventory.resolvePricing({ erpNumber, pplPricing }, quantity);
          const price = selectedPricing?.Pricing || 0;
          if (!price)
            continue;
          const ecoFee = resolveEcoFee(ecoFeeData);
          const dataToChange = await Cart.checkItemExists({
            id: variantId,
            quantity,
            erpNumber,
            price,
            fresh: false
          });
          if (dataToChange) {
            changesToApply.push(dataToChange);
          } else {
            itemsToAdd.push(checkAndUpdateQuantity({ id: variantId, properties: Cart.buildItemProperties(selectedPricing, ecoFee) }, price, quantity));
          }
        }
        const sections = Cart.addDefaultSections(variantIds);
        if (itemsToAdd.length > 0) {
          await Cart.batchAdd(itemsToAdd, changesToApply.length === 0 ? sections : null);
        }
        if (changesToApply.length > 0) {
          await Cart.batchChange(changesToApply, sections);
        }
      } catch (error) {
        this.setState("error", error.message);
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    async handleDeleteSelected() {
      try {
        this.setState("loading");
        const selectedItems = new Set(this.selectedVariants);
        this.selectedVariants.clear();
        selectedItems.forEach((variantId) => this.cardCache.delete(variantId));
        const itemsToUpdate = this.jobListInfo.items.filter(
          (item) => !selectedItems.has(variantIdFromGid(item.variantGid))
        );
        const dataToDelete = {
          customerGid: this.jobListInfo.customerGid,
          name: this.jobListInfo.name,
          description: this.jobListInfo.description,
          items: itemsToUpdate
        };
        const updatedJobList = await JobLists.update(this.jobListId, dataToDelete);
        this.jobListInfo = updatedJobList;
        this.updateLayout();
      } catch (error) {
        throw error;
      } finally {
        this.setState("reset");
      }
    }
    hideError() {
      this.classList.remove("is-error");
    }
    setState(state, message) {
      switch (state) {
        case "loading":
          this.classList.add("is-loading");
          break;
        case "exporting":
          this.classList.add("is-loading", "is-exporting");
          break;
        case "copying":
          this.classList.add("is-copying");
          break;
        case "importing":
          this.classList.add("is-importing");
          break;
        case "imported":
          this.classList.remove("is-importing");
          break;
        case "error":
          if (this.errorMessageElm && message != null)
            this.errorMessageElm.textContent = message;
          this.classList.add("is-error");
          break;
        case "atc":
          this.classList.add("is-atc-loading", "is-loading");
          break;
        case "owner":
          this.classList.remove("readonly");
          this.classList.add("is-owner");
          break;
        case "readonly":
          this.classList.add("readonly");
          break;
        default:
          this.classList.remove("is-loading", "is-exporting", "is-importing", "is-atc-loading", "is-copying");
          break;
      }
    }
    isOwner() {
      return this.classList.contains("is-owner");
    }
    isReadOnly() {
      return this.classList.contains("readonly");
    }
    setImporterAlreadyExists() {
      this.productImporter.setAlreadyExists(this.jobListInfo.items.map((item) => ({ ec_product_id: item.variantGid })));
    }
  };
  function convertToHandle(str) {
    return str.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  }
  customElements.define("job-list-details", JobListDetails);
})();
